/**
 * Habitia Design Tokens
 * Sistema de diseño unificado para toda la plataforma admin.
 * Fuente de verdad: Normativa_Habitia.pdf + index.css CSS variables.
 *
 * REGLA: Importar estos tokens en vez de hardcodear valores.
 */

// ─── Colores ─────────────────────────────────────────────────────────────────

export const colors = {
  // Marca (Normativa A4)
  primary: "#0080ff",      // acento de marca: links, títulos, active states
  primaryDark: "#0066cc",  // fondo del botón primario (AA: blanco sobre #0066cc ≈ 5.5:1; sobre #0080ff era 3.8:1 y fallaba)
  primaryDarker: "#0052a3", // hover del botón primario
  primaryLight: "#4da6ff",
  primaryLighter: "#e8f4ff",
  primarySubtle: "#f0f7ff",

  // Estado: éxito
  success: "#10b981",
  successLight: "#d1fae5",
  successDark: "#065f46",

  // Estado: advertencia
  warning: "#f59e0b",
  warningLight: "#fef3c7",
  warningDark: "#92400e",

  // Estado: error / peligro
  error: "#ef4444",
  errorLight: "#fee2e2",
  errorDark: "#991b1b",

  // Estado: información
  info: "#3b82f6",
  infoLight: "#dbeafe",
  infoDark: "#1e40af",

  // Neutros
  black: "#1a1a1a",
  gray900: "#374151",
  gray700: "#4b5563",
  gray600: "#6b7280",
  gray400: "#9ca3af",
  gray300: "#d1d5db",
  gray200: "#e5e7eb",
  gray100: "#f3f4f6",
  gray50: "#f8f9fa",
  white: "#ffffff",

  // Fondos de página
  bgMain: "#f5f7fa",
  bgCard: "#ffffff",

  // Sidebar admin (dark)
  sidebarBg: "#1a1a1a",
  sidebarText: "rgba(255,255,255,0.7)",
  sidebarTextActive: "#ffffff",
  sidebarAccent: "#0080ff",
};

// ─── Sombras ─────────────────────────────────────────────────────────────────

export const shadows = {
  xs: "0 1px 2px rgba(0,0,0,0.04)",
  sm: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
  md: "0 4px 12px rgba(0,0,0,0.08)",
  lg: "0 8px 24px rgba(0,0,0,0.12)",
  modal: "0 20px 48px rgba(0,0,0,0.18)",
};

// ─── Border Radius ───────────────────────────────────────────────────────────

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  full: 9999,
};

// ─── Espaciado ───────────────────────────────────────────────────────────────

export const spacing = {
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
};

// ─── Tipografía ──────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  monoFamily: "'SF Mono', 'Fira Code', monospace",

  sizes: {
    xs: 11,
    sm: 12,
    base: 13,
    md: 14,
    lg: 16,
    xl: 18,
    "2xl": 22,
    "3xl": 28,
  },

  // Solo un peso: FT Habit Book (400).
  // Jerarquía se crea con color + tamaño + uppercase.
  weights: {
    normal: 400,
  },
};

// ─── Z-index ─────────────────────────────────────────────────────────────────

export const zIndex = {
  dropdown: 100,
  sticky: 200,
  sidebar: 300,
  modalBackdrop: 900,
  modal: 1000,
  toast: 1100,
};

// ─── Transiciones ────────────────────────────────────────────────────────────

export const transitions = {
  fast: "0.15s ease",
  normal: "0.2s ease",
  slow: "0.3s ease",
};

// ─── Estilos base reutilizables ──────────────────────────────────────────────

/** Overlay de modal unificado */
export const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: spacing[4],
  zIndex: zIndex.modal,
};

/** Card base — borderless, separacion por sombra */
export const cardBase = {
  background: colors.bgCard,
  borderRadius: radius.md,
  padding: spacing[5],
  boxShadow: shadows.sm,
  transition: `all ${transitions.normal}`,
};

/** Input base */
export const inputBase = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: radius.sm,
  border: `1px solid ${colors.gray200}`,
  fontSize: typography.sizes.md,
  fontFamily: typography.fontFamily,
  outline: "none",
  transition: `border-color ${transitions.fast}, box-shadow ${transitions.fast}`,
  backgroundColor: colors.white,
};

/** Botón primario */
export const btnPrimary = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing[2],
  padding: "10px 20px",
  borderRadius: radius.sm,
  border: "none",
  background: colors.primaryDark, // AA: blanco sobre este azul ≈ 5.5:1 (accesibilidad 30–90)
  color: colors.white,
  fontSize: typography.sizes.md,
  fontWeight: 400,
  fontFamily: typography.fontFamily,
  cursor: "pointer",
  transition: `all ${transitions.fast}`,
};

/** Botón secundario */
export const btnSecondary = {
  ...btnPrimary,
  background: colors.white,
  color: colors.gray900,
  border: `1px solid ${colors.gray200}`,
};

/** Botón peligro */
export const btnDanger = {
  ...btnPrimary,
  background: colors.errorLight,
  color: colors.errorDark,
};

/** Botón ghost (solo icono) */
export const btnGhost = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: spacing[2],
  borderRadius: radius.sm,
  border: "none",
  background: "transparent",
  color: colors.gray600,
  cursor: "pointer",
  transition: `all ${transitions.fast}`,
};

/** Encabezado de tabla — azul primary con texto blanco */
export const tableHeader = {
  textAlign: "left",
  padding: "12px 16px",
  background: colors.primary,
  fontWeight: 400,
  fontSize: typography.sizes.xs,
  color: colors.white,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderBottom: `1px solid ${colors.gray200}`,
};

/** Fila par de tabla (zebra striping) */
export const tableRowEven = {
  background: colors.gray50,
};

/** Celda de tabla */
export const tableCell = {
  padding: "12px 16px",
  fontSize: typography.sizes.md,
  color: colors.gray900,
  borderBottom: `1px solid ${colors.gray100}`,
};

/** Página container */
export const pageContainer = {
  padding: spacing[6],
  backgroundColor: colors.bgMain,
  minHeight: "100vh",
  fontFamily: typography.fontFamily,
};

/** Título de página */
export const pageTitle = {
  fontSize: 20,
  fontWeight: 400,
  color: colors.primary,
  margin: 0,
  letterSpacing: "-0.01em",
};

/** Subtítulo de página */
export const pageSubtitle = {
  fontSize: typography.sizes.base,
  color: colors.gray600,
  margin: 0,
};

/** Badge de estado base */
export const badgeBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 12px",
  borderRadius: radius.full,
  fontSize: typography.sizes.sm,
  fontWeight: 400,
  lineHeight: 1.4,
};
